import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  LinearProgress,
  Card,
  CardContent,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Tabs,
  Tab,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Security as SecurityIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  DataObject as DataObjectIcon
} from '@mui/icons-material';
import { monitoringAPI } from '../services/api';
import SecurityManagement from '../components/SecurityManagement';

const MonitoringPage = () => {
  const [metrics, setMetrics] = useState(null);
  const [requestLogs, setRequestLogs] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [expandedLog, setExpandedLog] = useState(null);
  const [cacheStats, setCacheStats] = useState(null);
  const [appStats, setAppStats] = useState([]);
  
  // Security state
  const [securityStats, setSecurityStats] = useState(null);
  const [blockedIps, setBlockedIps] = useState([]);
  const [blockedIpsTotal, setBlockedIpsTotal] = useState(0);
  const [blockedIpsPage, setBlockedIpsPage] = useState(0);
  const [blockedIpsPerPage, setBlockedIpsPerPage] = useState(100);
  const [whitelistedIps, setWhitelistedIps] = useState([]);
  const [suspiciousIps, setSuspiciousIps] = useState([]);

  const fetchMetrics = async () => {
    try {
      const response = await monitoringAPI.getSystemMetrics();
      setMetrics(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching metrics:', err);
      setError('Ошибка загрузки метрик системы');
    } finally {
      setLoading(false);
    }
  };

  const fetchRequestLogs = async () => {
    try {
      const response = await monitoringAPI.getRequestLogs(100);
      setRequestLogs(response.data.reverse()); // Показываем последние сверху
    } catch (err) {
      console.error('Error fetching request logs:', err);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await monitoringAPI.getRequestStatistics();
      setStatistics(response.data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  };

  const fetchCacheStats = async () => {
    try {
      const response = await monitoringAPI.getCacheStats();
      setCacheStats(response.data);
    } catch (err) {
      console.error('Error fetching cache stats:', err);
    }
  };

  const fetchAppStats = async () => {
    try {
      const response = await monitoringAPI.getAppStatistics();
      setAppStats(response.data);
    } catch (err) {
      console.error('Error fetching app stats:', err);
    }
  };

  const fetchSecurityData = async (page = blockedIpsPage) => {
    try {
      const skip = page * blockedIpsPerPage;
      const [stats, blocked, whitelisted, suspicious] = await Promise.all([
        monitoringAPI.getSecurityStats(),
        monitoringAPI.getBlockedIps(skip, blockedIpsPerPage),
        monitoringAPI.getWhitelistedIps(),
        monitoringAPI.getSuspiciousIps()
      ]);
      setSecurityStats(stats.data);
      setBlockedIps(blocked.data.items);
      setBlockedIpsTotal(blocked.data.total);
      setWhitelistedIps(whitelisted.data);
      setSuspiciousIps(suspicious.data);
    } catch (err) {
      console.error('Error fetching security data:', err);
    }
  };

  const handleBlockIp = async (ip, reason = 'Manual block') => {
    try {
      await monitoringAPI.blockIp(ip, reason);
      await fetchSecurityData();
    } catch (err) {
      console.error('Error blocking IP:', err);
      alert('Ошибка блокировки IP');
    }
  };

  const handleUnblockIp = async (ip) => {
    try {
      await monitoringAPI.unblockIp(ip);
      await fetchSecurityData();
    } catch (err) {
      console.error('Error unblocking IP:', err);
      alert('Ошибка разблокировки IP');
    }
  };

  const handleAddToWhitelist = async (ip) => {
    try {
      await monitoringAPI.addToWhitelist(ip);
      await fetchSecurityData();
    } catch (err) {
      console.error('Error adding to whitelist:', err);
      alert('Ошибка добавления в whitelist');
    }
  };

  const handleRemoveFromWhitelist = async (ip) => {
    try {
      await monitoringAPI.removeFromWhitelist(ip);
      await fetchSecurityData();
    } catch (err) {
      console.error('Error removing from whitelist:', err);
      alert('Ошибка удаления из whitelist');
    }
  };

  const handleClearSuspicious = async (ip) => {
    try {
      await monitoringAPI.clearSuspicious(ip);
      await fetchSecurityData();
    } catch (err) {
      console.error('Error clearing suspicious counter:', err);
      alert('Ошибка очистки счётчика');
    }
  };

  const handleClearLogs = async () => {
    if (window.confirm('Вы уверены, что хотите очистить все логи?')) {
      try {
        await monitoringAPI.clearLogs();
        setRequestLogs([]);
        setStatistics(null);
      } catch (err) {
        console.error('Error clearing logs:', err);
      }
    }
  };

  useEffect(() => {
    fetchMetrics();
    fetchRequestLogs();
    fetchStatistics();
    fetchSecurityData();
    fetchCacheStats();
    
    // Обновляем метрики каждые 3 секунды
    const metricsInterval = setInterval(() => {
      fetchMetrics();
      fetchCacheStats();
    }, 3000);
    
    // Обновляем логи каждые 5 секунд
    const logsInterval = setInterval(() => {
      fetchRequestLogs();
      fetchStatistics();
      fetchAppStats();
    }, 5000);
    
    // Обновляем данные безопасности каждые 10 секунд
    const securityInterval = setInterval(fetchSecurityData, 10000);
    
    return () => {
      clearInterval(metricsInterval);
      clearInterval(logsInterval);
      clearInterval(securityInterval);
    };
  }, []);

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getProgressColor = (percent) => {
    if (percent < 60) return 'success';
    if (percent < 80) return 'warning';
    return 'error';
  };

  const getStatusColor = (status) => {
    if (status < 300) return 'success';
    if (status < 400) return 'info';
    if (status < 500) return 'warning';
    return 'error';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('ru-RU');
  };

  if (loading && !metrics) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Мониторинг системы
        </Typography>
        <Button
          startIcon={<DeleteIcon />}
          onClick={handleClearLogs}
          color="error"
          variant="outlined"
        >
          Очистить логи
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Системные метрики */}
      {metrics && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* CPU */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SpeedIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6">Процессор</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {metrics.cpu.count} ядер
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">Загрузка</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {metrics.cpu.percent.toFixed(1)}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={metrics.cpu.percent} 
                    color={getProgressColor(metrics.cpu.percent)}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* RAM */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <MemoryIcon sx={{ fontSize: 40, mr: 2, color: 'secondary.main' }} />
                  <Box>
                    <Typography variant="h6">Оперативная память</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatBytes(metrics.memory.total)}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">Использовано</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {metrics.memory.percent.toFixed(1)}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={metrics.memory.percent} 
                    color={getProgressColor(metrics.memory.percent)}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Disk */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <StorageIcon sx={{ fontSize: 40, mr: 2, color: 'info.main' }} />
                  <Box>
                    <Typography variant="h6">Диск</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatBytes(metrics.disk.total)}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">Использовано</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {metrics.disk.percent.toFixed(1)}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={metrics.disk.percent} 
                    color={getProgressColor(metrics.disk.percent)}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Redis Cache */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <DataObjectIcon sx={{ fontSize: 40, mr: 2, color: 'success.main' }} />
                  <Box>
                    <Typography variant="h6">Redis Cache</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {cacheStats ? cacheStats.redis_version : 'Загрузка...'}
                    </Typography>
                  </Box>
                </Box>
                
                {cacheStats && (
                  <>
                    <Box sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">Всего ключей</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {cacheStats.total_keys}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">Память</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {cacheStats.used_memory_human}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'text.secondary' }}>
                      <span>config: {cacheStats.config_keys}</span>
                      <span>geo: {cacheStats.geo_keys}</span>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Табы для логов, статистики и безопасности */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label={`Логи запросов (${requestLogs.length})`} />
          <Tab label="Статистика эндпоинтов" />
          <Tab icon={<DataObjectIcon />} label={`Приложения (${appStats.length})`} iconPosition="start" />
          <Tab icon={<SecurityIcon />} label="Безопасность" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Логи запросов */}
      {tabValue === 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="50px"></TableCell>
                <TableCell>Время</TableCell>
                <TableCell>Метод</TableCell>
                <TableCell>Путь</TableCell>
                <TableCell>IP</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Время (мс)</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requestLogs.map((log, index) => (
                <React.Fragment key={log.timestamp}>
                  <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpandedLog(expandedLog === log.timestamp ? null : log.timestamp)}>
                    <TableCell>
                      <IconButton size="small">
                        {expandedLog === log.timestamp ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{formatTimestamp(log.timestamp)}</TableCell>
                    <TableCell>
                      <Chip label={log.method} size="small" color="primary" />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{log.path}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{log.client_ip}</TableCell>
                    <TableCell>
                      <Chip label={log.status_code} size="small" color={getStatusColor(log.status_code)} />
                    </TableCell>
                    <TableCell>{log.duration_ms.toFixed(2)}</TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<BlockIcon />}
                        onClick={() => {
                          if (window.confirm(`Заблокировать IP ${log.client_ip}?`)) {
                            handleBlockIp(log.client_ip, `Blocked from logs: ${log.method} ${log.path} (${log.status_code})`);
                          }
                        }}
                        disabled={log.client_ip === '127.0.0.1' || log.client_ip === '::1'}
                      >
                        Блок
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0 }}>
                      <Collapse in={expandedLog === log.timestamp}>
                        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="subtitle2" gutterBottom>Заголовки:</Typography>
                          <Paper sx={{ p: 1, mb: 2, bgcolor: 'white' }}>
                            <pre style={{ margin: 0, fontSize: '0.75rem', overflow: 'auto' }}>
                              {JSON.stringify(log.headers, null, 2)}
                            </pre>
                          </Paper>
                          
                          {log.query_params && Object.keys(log.query_params).length > 0 && (
                            <>
                              <Typography variant="subtitle2" gutterBottom>Query параметры:</Typography>
                              <Paper sx={{ p: 1, mb: 2, bgcolor: 'white' }}>
                                <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                                  {JSON.stringify(log.query_params, null, 2)}
                                </pre>
                              </Paper>
                            </>
                          )}
                          
                          {log.body && (
                            <>
                              <Typography variant="subtitle2" gutterBottom>Тело запроса:</Typography>
                              <Paper sx={{ p: 1, bgcolor: 'white' }}>
                                <pre style={{ margin: 0, fontSize: '0.75rem', overflow: 'auto' }}>
                                  {typeof log.body === 'object' ? JSON.stringify(log.body, null, 2) : log.body}
                                </pre>
                              </Paper>
                            </>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Статистика эндпоинтов */}
      {tabValue === 1 && statistics && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Эндпоинт</TableCell>
                <TableCell align="right">Запросов</TableCell>
                <TableCell align="right">Среднее время (мс)</TableCell>
                <TableCell align="right">Ошибок</TableCell>
                <TableCell align="right">% ошибок</TableCell>
                <TableCell>Коды ответов</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {statistics.endpoints.map((stat, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{stat.endpoint}</TableCell>
                  <TableCell align="right">{stat.count}</TableCell>
                  <TableCell align="right">{stat.avg_duration_ms.toFixed(2)}</TableCell>
                  <TableCell align="right">{stat.errors}</TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={`${stat.error_rate.toFixed(1)}%`} 
                      size="small" 
                      color={stat.error_rate > 10 ? 'error' : stat.error_rate > 5 ? 'warning' : 'success'}
                    />
                  </TableCell>
                  <TableCell>
                    {Object.entries(stat.status_codes).map(([code, count]) => (
                      <Chip 
                        key={code}
                        label={`${code}: ${count}`} 
                        size="small" 
                        color={getStatusColor(parseInt(code))}
                        sx={{ mr: 0.5 }}
                      />
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Статистика приложений */}
      {tabValue === 2 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Приложение (Bundle)</TableCell>
                <TableCell align="right">Запросов</TableCell>
                <TableCell>Версии</TableCell>
                <TableCell>Модели устройств</TableCell>
                <TableCell>Последняя активность</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appStats.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">
                      Нет данных по приложениям
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                appStats.map((app, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {app.bundle}
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={app.count} color="primary" />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {Object.entries(app.versions)
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 5)
                          .map(([version, count]) => (
                            <Chip 
                              key={version}
                              label={`v${version} (${count})`}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        {Object.keys(app.versions).length > 5 && (
                          <Chip 
                            label={`+${Object.keys(app.versions).length - 5}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {Object.entries(app.models)
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 3)
                          .map(([model, count]) => (
                            <Chip 
                              key={model}
                              label={`${decodeURIComponent(model)} (${count})`}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        {Object.keys(app.models).length > 3 && (
                          <Chip 
                            label={`+${Object.keys(app.models).length - 3}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {app.last_seen ? new Date(app.last_seen).toLocaleString('ru-RU') : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Безопасность */}
      {tabValue === 3 && securityStats && (
        <Grid container spacing={3}>
          {/* Статистика безопасности */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Статистика безопасности
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Заблокировано IP
                      </Typography>
                      <Typography variant="h4" color="error">
                        {blockedIpsTotal}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Whitelist IP
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {securityStats.whitelisted_ips_count}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Подозрительные IP
                      </Typography>
                      <Typography variant="h4" color="warning.main">
                        {securityStats.suspicious_ips_count}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Порог автоблокировки
                      </Typography>
                      <Typography variant="h4">
                        {securityStats.auto_block_threshold}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Управление блокировками */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BlockIcon sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6">
                  Управление блокировками IP
                </Typography>
              </Box>
              <SecurityManagement 
                blockedIps={blockedIps}
                suspiciousIps={suspiciousIps}
                whitelistedIps={whitelistedIps}
                onRefresh={fetchSecurityData}
                page={blockedIpsPage}
                rowsPerPage={blockedIpsPerPage}
                totalRows={blockedIpsTotal}
                onPageChange={(newPage) => {
                  setBlockedIpsPage(newPage);
                  fetchSecurityData(newPage);
                }}
                onRowsPerPageChange={(newRowsPerPage) => {
                  setBlockedIpsPerPage(newRowsPerPage);
                  setBlockedIpsPage(0);
                  fetchSecurityData(0);
                }}
              />
            </Paper>
          </Grid>

          {/* Подозрительные IP */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WarningIcon sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6">
                  Подозрительные IP ({suspiciousIps.length})
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>IP адрес</TableCell>
                      <TableCell>Счётчик</TableCell>
                      <TableCell align="right">Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {suspiciousIps.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          <Typography color="text.secondary">
                            Нет подозрительных IP
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      suspiciousIps.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{item.ip}</TableCell>
                          <TableCell>
                            <Chip 
                              label={`${item.count} / ${securityStats.auto_block_threshold}`} 
                              size="small" 
                              color={item.count >= securityStats.auto_block_threshold * 0.7 ? 'error' : 'warning'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button 
                              size="small" 
                              onClick={() => handleBlockIp(item.ip, 'Manual block from suspicious list')}
                              color="error"
                              sx={{ mr: 1 }}
                            >
                              Заблокировать
                            </Button>
                            <Button 
                              size="small" 
                              onClick={() => handleClearSuspicious(item.ip)}
                              variant="outlined"
                            >
                              Очистить
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Whitelist IP */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircleIcon sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="h6">
                  Whitelist IP ({whitelistedIps.length})
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {whitelistedIps.map((ip, index) => (
                  <Chip
                    key={index}
                    label={ip}
                    color="success"
                    variant="outlined"
                    onDelete={ip !== '127.0.0.1' && ip !== '::1' ? () => handleRemoveFromWhitelist(ip) : undefined}
                  />
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Метрики обновляются каждые 3 секунды • Логи обновляются каждые 5 секунд • Безопасность обновляется каждые 10 секунд
        </Typography>
      </Paper>
    </Container>
  );
};

export default MonitoringPage;
